import React from 'react'
import { Link } from 'react-router-dom'
import { MbscCalendarEvent, Popup } from '@mobiscroll/react-latest'
import moment from 'moment'
import Point from '../../icons/ui/Point'
import ArrowRightWithTail from '../../icons/ui/ArrowRightWithTail'
import { MoonLoader } from 'react-spinners'
import { IDriveTimesDTO } from 'src/types'
import { secondsToMinutes } from 'src/utils'
import { options } from 'src/constants/trip'

interface ITimelineEventPopupProps {
  driveTimes: IDriveTimesDTO[]
  eventInfo: MbscCalendarEvent | undefined
  isTooltipOpen: boolean
  tooltipAnchor: HTMLElement | undefined
  loading: boolean
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleTooltipClose: () => void
}

interface ILocationDetails {
  address: string
  placeName: string
  type: string
  time: string | object | Date | undefined
}

interface ITripLocationDetailsProps {
  location: ILocationDetails
}

const TripLocationDetails = ({ location }: ITripLocationDetailsProps) => {
  const { address, placeName, type, time: eta } = location
  const icon = {
    origin: 'svg-green',
    additionalStop: 'svg-blue',
    destination: 'svg-red'
  }

  const time = moment(eta).isValid() ? moment(eta).format('hh:mm A') : ''

  return (
    <div className='flex pb2 tmln-popup-location-container w-100'>
      <div className='tmln-popup-location-icon mt01'>
        <Point className={`${icon[type as keyof typeof icon]} icon--36x36`} />
      </div>
      <div className='tmln-popup-location-placename'>
        <span className=''>{placeName}</span>
      </div>
      <div className='tmln-popup-location fs-14'>
        <span className=''>{address}</span>
      </div>
      <div className='tmln-popup-location-time fs-14'>
        <span className=''>{time}</span>
      </div>
    </div>
  )
}

const TimelineEventPopup = ({
  driveTimes,
  eventInfo,
  isTooltipOpen,
  tooltipAnchor,
  loading,
  handleMouseEnter,
  handleMouseLeave,
  handleTooltipClose
}: ITimelineEventPopupProps) => {
  if (eventInfo === undefined || !isTooltipOpen) {
    return null
  }

  const parsePlaceName = (address: string) => {
    return address.split(',')[0]
  }

  const getPlaceName = (location: MbscCalendarEvent) => {
    return location.placeName ? location.placeName : parsePlaceName(location.fullAddress)
  }

  const getAdditionalStopDetails = (destinations: MbscCalendarEvent[]) => {
    if (!!destinations.length) {
      const additionalStop = destinations.map((additionalStop: MbscCalendarEvent) => {
        const { location: stopLocation } = additionalStop
        return {
          address: stopLocation.fullAddress,
          placeName: getPlaceName(stopLocation),
          time: '',
          type: 'additionalStop'
        }
      })
      return additionalStop
    }
    return []
  }

  const {
    color,
    destinations,
    dropOffLocation,
    end: dropOffTime,
    id: tripId,
    pickupLocation,
    product,
    start: pickUpTime,
    trip
  } = eventInfo
  const notes = (trip.notes || '')
    .split('\n')
    .filter((value: string) => value !== '' && value !== ';')
    .join()
  const pickUpPlaceName = getPlaceName(pickupLocation)
  const dropOffPlaceName = getPlaceName(dropOffLocation)
  const { name: productName } = product
  const locationArr = [
    { address: pickupLocation.fullAddress, placeName: pickUpPlaceName, time: pickUpTime, type: 'origin' },
    ...(destinations !== undefined ? getAdditionalStopDetails(destinations) : []),
    { address: dropOffLocation.fullAddress, placeName: dropOffPlaceName, time: dropOffTime, type: 'destination' }
  ]

  const tripDriveTimes = driveTimes.filter(time => time.tripId === tripId)[0]
  const { duration = 0 } = tripDriveTimes || {}

  const getStatusLabel = (value: string) => {
    const status = options.find(option => option.value === value)

    return status ? status.label : 'NO STATUS'
  }

  return (
    <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Popup
        anchor={tooltipAnchor}
        contentPadding={false}
        cssClass='tmln-event-popup'
        display='anchored'
        isOpen={isTooltipOpen}
        scrollLock={false}
        showArrow={false}
        showOverlay={false}
        touchUi={false}
        width={550}
        onClose={handleTooltipClose}
      >
        <Link className='tmln-popup-trip-link' to={`/trips/${tripId}`}>
          <div className='mds-tooltip'>
            <div className='mds-tooltip-header p2 flex flex-row justify-between' style={{ backgroundColor: color }}>
              <div className='flex-column'>
                <div className='font-white weight-600'>
                  {`${pickUpPlaceName}`}
                  <ArrowRightWithTail className='icon--16x16 px1' fill='white' />
                  {`${dropOffPlaceName}`}
                </div>
                <div className='font-white weight-300 pt1'>{productName}</div>
              </div>
              <div className='mds-tooltip-status'>{getStatusLabel(eventInfo.lastStatus)}</div>
            </div>
            <div className='mbsc-padding tmln-popup-body'>
              {loading === false ? (
                <>
                  {locationArr.map((location, index) => (
                    <TripLocationDetails key={`${location.address}-${index}`} location={location} />
                  ))}
                  {notes && <span>Notes : {notes}</span>}
                  {!!duration && <p>{`Drive time to the next pick up location: ${secondsToMinutes(duration)}`}</p>}
                </>
              ) : (
                <div className='tmln-popup-loading'>
                  <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
                </div>
              )}
            </div>
          </div>
        </Link>
      </Popup>
    </span>
  )
}

export default TimelineEventPopup



// WEBPACK FOOTER //
// ./src/components/trips/timeline-view/TimelineEventPopup.tsx